import { useRef, useEffect, useState, useMemo } from 'react';

import type { Prompt } from '../../../interface/Prompt';
import { Probe } from '../../../interface/Probe';
import type {
    RepeatAudioCallbacks,
    InterruptAudioCallbacks,
    EncourageAudioCallbacks,
    PlayVariantAudioCallbacks,
    PlayPromptAudioCallbacks,
} from '../../../interface/PlayAudio';
import { useAudioPlayer } from './useAudioPlayer';
import { PartRules } from '../../../interface/Configuration';
import { pushFaroEvent } from '../../../faroSession';

interface Options {
    currentPrompt: Prompt;
    probeList: Probe[];
    currentPartRules: PartRules;
}

const MAX_VARIANT_PLAY = 1; // Maximum variant plays for a prompt

export const useAudioActions = ({ currentPrompt, probeList, currentPartRules }: Options) => {
    const { playAudio, audioRef, pause, isPlayingRef } = useAudioPlayer();

    const [error, setError] = useState<string | null>(null);

    const repeatCounterRef = useRef(0);
    const encourageCounterRef = useRef(0);
    const encourageRef = useRef(false);
    const currentEncourageIdx = useRef(0);
    const variantCounterRef = useRef(0);

    const { shortResponse, repeatStop } = currentPartRules;
    const MAX_ENCROURAGE = shortResponse.maxEncourage; // Maximum encourages for a prompt
    const MAX_REPEATS = repeatStop.maxRepeat; // Maximum repeat for a prompt
    const partProbeList = probeList.filter((pb) => pb.parts[0].split(',').includes(currentPrompt?.partName));

    const encourageProbes = useMemo(() => partProbeList.filter((pb) => pb.type === 'encourage'), [partProbeList]);
    
    // Play prompt
    const playPrompt = (callbacks: PlayPromptAudioCallbacks, force?: boolean) => {
        if (!currentPrompt) return;

        const { onPlay, onEnded, onError } = callbacks;
        const audioFileUrl = `${currentPrompt.localizedMedia.audioPathUrl}/${currentPrompt.localizedMedia.audioRef}`;

        playAudio(currentPrompt.localizedMedia.audioPreSignUrl, {
            force,
            onPlay: () => {
                onPlay?.(audioFileUrl);
            },
            onEnded,
            onError: (error) => {
                console.error('Error playing prompt audio:', error);
                setError('Error playing prompt audio');
                onError?.(error);
            },
        });
    };

    // Repeat current prompt
    const repeat = (callbacks: RepeatAudioCallbacks) => {
        const probe = partProbeList.find((pb) => pb.type === 'repeat');

        if (!probe) return;

        const { onPlay, onEnded, onError, onMaxRepeat, onRepeat } = callbacks;
        const audioFileUrl = `${probe.audioPathUrl}/${probe.audioRef}`;

        if (!isPlayingRef.current && repeatCounterRef.current >= MAX_REPEATS) {
            onMaxRepeat?.();
        } else {
            playAudio(probe.audioPreSignUrl, {
                onPlay: () => {
                    repeatCounterRef.current += 1;
                    onPlay?.(audioFileUrl);
                },
                onEnded: () => {
                    playPrompt(
                        {
                            onPlay: (audioFileUrl) => {
                                onRepeat?.(audioFileUrl);
                            },
                            onEnded,
                        },
                        true,
                    );
                },
                onError: (error) => {
                    console.error('Error playing repeat probe audio:', error);
                    setError('Error playing repeat probe audio');
                    onError?.(error);
                },
            });
        }
      
    };

    // Interrupt
    const interrupt = (callbacks: InterruptAudioCallbacks) => {
        const probe = partProbeList.find((pb) => pb.type === 'interrupt');

        if (!probe) return;

        const { onPlay, onEnded, onError } = callbacks;
        const audioFileUrl = `${probe.audioPathUrl}/${probe.audioRef}`;

        playAudio(probe.audioPreSignUrl, {
            onPlay: () => {
                onPlay?.(audioFileUrl);
            },
            onEnded,
            onError: (error) => {
                console.error('Error playing interrupt probe audio:', error);
                setError('Error playing interrupt probe audio');
                onError?.(error);
            },
        });
    };

    // Encourage
    const encourage = (callbacks: EncourageAudioCallbacks) => {
        const probe = encourageProbes[currentEncourageIdx.current];
    
        if (!probe) return;

        const { onPlay, onEnded, onError, onMaxEncourage } = callbacks;
        const audioFileUrl = `${probe.audioPathUrl}/${probe.audioRef}`;

        if (!isPlayingRef.current && encourageCounterRef.current >= MAX_ENCROURAGE) {
            onMaxEncourage?.();
        } else {
            playAudio(probe.audioPreSignUrl, {
                onPlay: () => {
                    currentEncourageIdx.current = (currentEncourageIdx.current + 1) % encourageProbes.length;
                    encourageCounterRef.current += 1;
                    onPlay?.(audioFileUrl);
                },
                onEnded,
                onError: (error) => {
                    console.error('Error playing encourage probe audio:', error);
                    setError('Error playing encourage probe audio');
                    onError?.(error);
                },
            });
        }
    };

    // Play variant
    const playVariant = (callbacks: PlayVariantAudioCallbacks) => {
        const { onPlay, onEnded, onError, onMaxVariantsPlayed, onPlayVariant } = callbacks;
        const variant = currentPrompt?.variants?.find((v) => v.type === 'simplified');

        if (!variant)
            return repeat({ onPlay, onEnded, onError, onMaxRepeat: onMaxVariantsPlayed, onRepeat: onPlayVariant });

        const probe = partProbeList.find((pb) => pb.type === 'reword');

        if (!probe) return;

        const variantAudioFileUrl = `${variant.localizedMedia.audioPathUrl}/${variant.localizedMedia.audioRef}`;
        const probeAudioFileUrl = `${probe.audioPathUrl}/${probe.audioRef}`;

        if (!isPlayingRef.current && variantCounterRef.current >= MAX_VARIANT_PLAY) {
            onMaxVariantsPlayed?.();
        } else {
            playAudio(probe.audioPreSignUrl, {
                onPlay: () => {
                    variantCounterRef.current += 1;
                    onPlay?.(probeAudioFileUrl);
                },
                onEnded: () => {
                    playAudio(variant.localizedMedia.audioPreSignUrl, {
                        force: true,
                        onPlay: () => {
                            onPlayVariant?.(variantAudioFileUrl);
                        },
                        onEnded,
                    });
                },
                onError: (error) => {
                    console.error('Error playing variant audio:', error);
                    setError('Error playing variant audio');
                    onError?.(error);
                },
            });
        }
    };

    const playBackupVariant = (callbacks: PlayVariantAudioCallbacks) => {
        const { onPlay, onEnded, onError, onMaxVariantsPlayed, onPlayVariant } = callbacks;
        const variant = currentPrompt?.variants?.find((v) => v.type === 'backup');

        if (!variant)
            return repeat({ onPlay, onEnded, onError, onMaxRepeat: onMaxVariantsPlayed, onRepeat: onPlayVariant });

        const variantAudioFileUrl = `${variant.localizedMedia.audioPathUrl}/${variant.localizedMedia.audioRef}`;

        if (variantCounterRef.current >= MAX_VARIANT_PLAY) {
            onMaxVariantsPlayed?.();
        } else {
            playAudio(variant.localizedMedia.audioPreSignUrl, {
                onPlay: () => {
                    variantCounterRef.current += 1;
                    onPlayVariant?.(variantAudioFileUrl);
                },
                onEnded,
                onError: (error) => {
                    console.error('Error playing variant audio:', error);
                    setError('Error playing variant audio');
                    onError?.(error);
                },
            });



        }
    };

    const reset = () => {
        repeatCounterRef.current = 0;
        encourageCounterRef.current = 0;
        encourageRef.current = false;
        variantCounterRef.current = 0;
    };

    const playBeep = (callbacks: PlayPromptAudioCallbacks) => {
        playAudio('/assets/audios/beep.mp3', {

            onEnded: () => {
                pushFaroEvent('beep_audio_ended');
                callbacks.onEnded?.();
            },
            onError: (error) => {
                console.error('Error playing beep audio:', error);
                setError('Error playing beep audio');
            },
        });
    }

    useEffect(() => {
        return () => {
            currentEncourageIdx.current = 0;
            reset();
        };
    }, []);

    return {
        encourage,
        repeat,
        interrupt,
        playVariant,
        playPrompt,
        resetRefs: reset,
        pause,
        playBeep,
        encourageRef,
        audioRef,
        isPlayingRef,
        error,
        playBackupVariant,
    };
};
